import { createContext, useContext, useEffect, useState, useMemo } from "react";
import { api } from "./api";

/**
 * SiteContent — frontend CMS provider.
 *
 * Fetches all CMS sections in one batch request on mount, exposes a `useCMS`
 * hook that returns a merged view: CMS value → default value → undefined.
 *
 *   const brand = useCMS("brand", BRAND_DEFAULTS);
 *
 * Components never crash if the CMS is empty — defaults always render.
 */
const CMSContext = createContext({ ready: false, sections: {}, reload: () => {} });

export function SiteContentProvider({ children }) {
  const [sections, setSections] = useState({});
  const [ready, setReady] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/site_content");
      setSections(data || {});
    } catch (e) {
      // Network/server hiccup — fall back to in-component defaults
      console.warn("[cms] could not load site content, using defaults", e?.message);
      setSections({});
    } finally {
      setReady(true);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo(() => ({ ready, sections, reload: load }), [ready, sections]);
  return <CMSContext.Provider value={value}>{children}</CMSContext.Provider>;
}

/**
 * Deep-merge defaults with CMS data. CMS values always win; missing keys fall
 * back to defaults. Arrays are NOT merged — CMS overrides the entire array.
 */
function mergeDeep(defaults, override) {
  if (override === undefined || override === null) return defaults;
  if (Array.isArray(defaults) || Array.isArray(override)) return override ?? defaults;
  if (typeof defaults !== "object" || typeof override !== "object") {
    return override !== undefined ? override : defaults;
  }
  const out = { ...defaults };
  for (const key of Object.keys(override)) {
    out[key] = mergeDeep(defaults?.[key], override[key]);
  }
  return out;
}

export function useCMS(sectionKey, defaults = {}) {
  const { sections } = useContext(CMSContext);
  const remote = sections?.[sectionKey];
  return useMemo(() => mergeDeep(defaults, remote), [defaults, remote]);
}

export function useCMSReady() {
  return useContext(CMSContext).ready;
}

export function useCMSReload() {
  return useContext(CMSContext).reload;
}
